import type { CalculationMode, FormulaBreakdown } from '@bioavail/shared-types';
import { CalculationMode as CM } from '@bioavail/shared-types';

export interface AbsoluteFInput {
  aucTest: number;
  aucRef: number;
  doseTest: number;
  doseRef: number;
}

export interface AbsoluteFResult {
  fRaw: number;
  fDoseCorrected: number;
  fPercent: number;
  doseNormalized: boolean;
}

const EPSILON = 1e-12;

/**
 * Compute absolute bioavailability F (PSD §4.2.1).
 * F = (AUC_test × Dose_ref) / (AUC_ref × Dose_test)
 */
export function computeAbsoluteF(input: AbsoluteFInput): AbsoluteFResult {
  const { aucTest, aucRef, doseTest, doseRef } = input;

  if (aucRef <= EPSILON) {
    throw new Error('Reference AUC is zero; cannot compute F (divide by zero).');
  }
  if (doseTest <= EPSILON || doseRef <= EPSILON) {
    throw new Error('Dose must be greater than zero.');
  }

  const rawRatio = aucTest / aucRef;
  const dosesDiffer = Math.abs(doseTest - doseRef) > EPSILON;
  const fDoseCorrected = dosesDiffer
    ? (aucTest * doseRef) / (aucRef * doseTest)
    : rawRatio;

  return {
    fRaw: rawRatio,
    fDoseCorrected,
    fPercent: fDoseCorrected * 100,
    doseNormalized: dosesDiffer,
  };
}

/**
 * Compute relative bioavailability F_rel (PSD §4.2.2).
 * Same formula; F_rel > 1 is valid (no f_exceeds_one warning in relative mode).
 */
export function computeRelativeF(input: AbsoluteFInput): AbsoluteFResult {
  // Same arithmetic; the difference is interpretation/warning policy.
  return computeAbsoluteF(input);
}

/**
 * Build the substituted formula breakdown (F-035) for either mode.
 */
export function buildFormula(
  mode: CalculationMode,
  selectedAucTest: number,
  selectedAucReference: number,
  doseTest: number,
  doseReference: number,
  fResult: AbsoluteFResult,
): FormulaBreakdown {
  const fFinal = fResult.fDoseCorrected;

  // Only the symbolic template and the substituted label prefix differ by mode;
  // the step construction is identical for both modes.
  const isAbsolute = mode === CM.Absolute;
  const label = isAbsolute ? 'F' : 'F_rel';
  const symbolic = isAbsolute
    ? 'F = (AUC_oral · Dose_IV) / (AUC_IV · Dose_oral)'
    : 'F_rel = (AUC_test · Dose_ref) / (AUC_ref · Dose_test)';

  const steps = [];
  const rawExpr = `AUC_test / AUC_ref = ${selectedAucTest} / ${selectedAucReference} = ${fResult.fRaw.toPrecision(6)}`;
  steps.push({ label: 'Raw ratio (AUC_test / AUC_ref)', expression: rawExpr });

  if (fResult.doseNormalized) {
    steps.push({
      label: 'Dose-normalize both arms',
      expression: `AUC_test/Dose_test = ${selectedAucTest}/${doseTest} = ${(selectedAucTest / doseTest).toPrecision(6)}; AUC_ref/Dose_ref = ${selectedAucReference}/${doseReference} = ${(selectedAucReference / doseReference).toPrecision(6)}`,
    });
  }

  const substituted = `${label} = (${selectedAucTest} · ${doseReference}) / (${selectedAucReference} · ${doseTest}) = ${fFinal.toPrecision(6)}`;

  return { symbolic, substituted, steps };
}
